import { AnimatePresence } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AnimatedPanel } from './components/AnimatedPanel';
import { AuthModal } from './components/AuthModal';
import { MobileNav, Sidebar } from './components/Sidebar';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { DiscoverPanel } from './pages/DiscoverPanel';
import { FriendsPanel } from './pages/FriendsPanel';
import { HomePanel } from './pages/HomePanel';
import { InventoryPanel } from './pages/InventoryPanel';
import { ProfilePanel } from './pages/ProfilePanel';
import { SettingsPanel } from './pages/SettingsPanel';
import { GameManagePanel } from './pages/GameManagePanel';
import type { PanelId } from './types';
import { useAuth } from './context/AuthContext';

function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [authMode, setAuthMode] = useState<'login' | 'register' | null>(null);

  const activePanel: PanelId = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/profile')) return 'profile';
    if (path === '/discover') return 'discover';
    if (path === '/friends') return 'friends';
    if (path === '/inventory') return 'inventory';
    if (path === '/settings') return 'settings';
    return 'home';
  }, [location.pathname]);

  function navigatePanel(panel: PanelId) {
    const routes: Record<PanelId, string> = {
      home: '/',
      discover: '/discover',
      friends: '/friends',
      inventory: '/inventory',
      settings: '/settings',
      profile: user ? `/profile/${user.username}` : '/profile',
    };
    navigate(routes[panel]);
  }

  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-korvex-accent border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <div className="hidden md:block">
        <Sidebar active={activePanel} onNavigate={navigatePanel} onAuth={setAuthMode} />
      </div>

      <main className="flex-1 overflow-y-auto pb-20 md:pb-8">
        <header className="sticky top-0 z-20 border-b border-white/5 bg-korvex-bg/80 px-6 py-4 backdrop-blur-xl md:hidden">
          <div className="flex items-center gap-3">
            <img src="/korvex.svg" alt="Korvex" className="h-9 w-9" />
            <div>
              <p className="font-display font-bold">Korvex</p>
              <p className="text-xs text-korvex-muted">playkorvex.com</p>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-6xl px-4 py-8 md:px-8">
          <AnimatedPanel panelKey={location.pathname}>
            <Routes>
              <Route path="/" element={<HomePanel onAuth={setAuthMode} onNavigate={navigatePanel} />} />
              <Route path="/discover" element={<DiscoverPanel />} />
              <Route path="/friends" element={<FriendsPanel />} />
              <Route path="/inventory" element={<InventoryPanel />} />
              <Route path="/settings" element={<SettingsPanel />} />
              <Route path="/games/:id" element={<GameManagePanel />} />
              <Route path="/profile" element={<ProfilePanel onNavigate={navigatePanel} />} />
              <Route path="/profile/:username" element={<ProfileRoute onNavigate={navigatePanel} />} />
            </Routes>
          </AnimatedPanel>
        </div>
      </main>

      <MobileNav active={activePanel} onNavigate={navigatePanel} />

      <AnimatePresence>
        {authMode && <AuthModal mode={authMode} onClose={() => setAuthMode(null)} onSwitch={setAuthMode} />}
      </AnimatePresence>
    </div>
  );
}

function ProfileRoute({ onNavigate }: { onNavigate: (panel: PanelId) => void }) {
  const { username } = useParams();
  return <ProfilePanel username={username} onNavigate={onNavigate} />;
}

export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <AppShell />
      </ThemeProvider>
    </AuthProvider>
  );
}
